import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { APP_ID } from "../utils/constants"
import { request } from "utils/request"
import { useAuthModalStore } from "./auth-modal"

export const HAS_LOGGED_IN_BEFORE = "hasLoggedInBefore"

const hasLoggedInBefore = () => {
  try {
    return sessionStorage.getItem(HAS_LOGGED_IN_BEFORE) === "true"
  } catch (_) {
    return false
  }
}

export interface IUser {
  userRoles: string[]
  userPermissions: IUserPermissions[]
  isPrimaryAdmin: boolean
  isSecondaryAdmin: boolean
  isAdmin: boolean
  isConsultant: boolean
  isActive: boolean
  userAvatarFileUrl: string
  userName: string
  userPhone: string
  userId: number
  userStatusId: number
  userFirstName: string
  userLastName: string
  userFullName: string
  lastLogin: string
  currentCompanyId: number
  isUserOrganisation: boolean
  userOrganisationId: number
  userCompanyId: number
  userOrganisation: string
  userOrganisationAlias: string | null
  userOrganisationEmail: string
  userOrganisationTelephone: string
  userOrganisationMobilePhone: string
  companyIdentifier: string
  nextInvoiceStartDate: string
  nextInvoiceStartDate_Short: string
  nextInvoiceEndDate: string
  nextInvoiceEndDate_Short: string
  lastPaymentDate: string | null
  lastPaymentDate_Short: string | null
  lastPaymentAmount: string | null
  lastPaymentInvoiceNumber: string | null
  hasPendingInvoice: boolean | null
  hasDueInvoice: boolean
  activeEmployees: number
  organisationChildren: number
  parentOrganisationId: string | null
  organisationPlan: string
  organisationPlanAmount: number
  organisationPlanBaseAmount: number
  nextInvoiceDaysRemaining: number
  startDate: string
  startDate_Short: string
  subscriptionId: string
  billingMode: string
  billingFrequency: string
  newBillingMode: string | null
  newBillingFrequency: string | null
  newOrganisationPlan: string | null
  secretKey: string
  threshold: string | null
  lastInvoiceId: string | null
  countryCode: string
  country: string
  currencyCode: string
  currencySymbol: string
  qualifiedCurrency: string
  billingCurrencyCode: string
  billingCurrencySymbol: string
  isBillingNotificationEnabled: boolean | null
  isHrisEnabled: boolean
  isPayrollEnabled: boolean
  isPerformanceEnabled: boolean
  isAtsEnabled: boolean
  isEwaEnabled: boolean
  isConfirmationEnabled: boolean
  isRetroPaymentEnabled: boolean
  splitPaymentEnabled: boolean
  timeTrackingEnabled: boolean
  customPlanAmount: string | null
  customPlanBaseAmount: string | null
  customPlanPayrollBilling: boolean | null
  customPlanFeatures: string | null
  databaseConfigKey: string
  buffer: number
  paystackPublicKey: string
  userWalletId: string
  organisationWalletId: string
  walletProvider: string
  setupStatus: string
  fileUploadLimit: number
  paymentProviderStatus: "UP" | "DOWN"
  adminsCanSwitchOrganisations: boolean
  coworkerDirectoryEnabled: boolean
  controlOrganisationId: number
  workingDays: string
  fixedWorkingDays: number
  unreadNotificationCount: number
  unreadNotifications: null
  userKey: string
  personKey: string
  accessToken: string | null
  refreshToken: string | null
  organisationAvatarFileUrl: string
  isAddressSetup: boolean
  isPaygradeSetup: boolean
  isEmployeeSetup: boolean
  isWalletSetup: boolean
  isStatutoryInfoSetup: boolean
  isDocumentSigned: boolean
  isCompliant: boolean
  setupPercentage: number
  setupStep: number
  supportEmail: string
  canSwitch: boolean
  isWebhookIntegrationEnabled: boolean
  isPasswordReset: boolean | null
  allowCrossReporting: boolean
  isManager: boolean
  externalLoanEnabled: boolean
  onDemandPayrollEnabled: boolean
  members: ISessionMember[]
  comapanyGroupEmail: string // note: typo in API response
}

interface ISessionMember {
  key: string
  id: string
  company_id: string
  parent_id: string
  name: string
}

export interface IUserPermissions {
  id: number
  name: string
  alias: string
  segment: string
}

export interface CheckUserSession {
  status: string
  refresh_token: string
  access_token: string
}

export type ILoginResponse = IBaseRes<ILogin>

export interface ILogin {
  access_token: string
  refresh_token: string
  status: string
  userid: string
  username: string
  message: string
  isOauth?: boolean
  callback_url?: string
  is_generated?: boolean
}

export interface ILoginWith2FA {
  access_token?: string
  refresh_token?: string
  status?: string
  user?: any
  message?: string | null
  userid: string
}

export interface ISendCodeResponse {
  data: string
  code: number
  message: string
  errors: Record<string, unknown>
  success: boolean
}

/**
 * @description - This type is used to extend the IUser type with additional properties that are not present in the backend response but are required for frontend logic. This helps to avoid type errors and makes it easier to manage user-related state in the application.
 */
export interface UserExtension {
  isHOD?: boolean
  isNewPerformanceSetupCompleted?: boolean
  isNewPerformanceEnabled?: boolean
  isLoadingInfo?: boolean
  isLoadingPerformanceTracker?: boolean
  isLoadingHODCheck?: boolean
  departmentId?: number
  departmentName?: string
}

interface AuthState {
  isAuthenticated: boolean
  authToken: string | null
  refreshToken: string | null
  user: (IUser & Partial<UserExtension>) | null
  authUserId: string | null
  referrer: string | null
  setup2FAToken: string | null
}

interface AuthMethods {
  clearSetup2FAToken: () => void
  signOut: (keepRedirect?: boolean) => void
  checkUserSession: () => Promise<CheckUserSession>
  changeOrganization: (
    access_token: any,
    newUser: IUser & Partial<UserExtension>
  ) => Promise<{ user: IUser & Partial<UserExtension> }>
  clearAuthDataStorage: () => void
  extendUser: (userData: Partial<UserExtension>) => void
}

export const useAuthStore = create<AuthState & AuthMethods>()(
  devtools(
    persist(
      (set, get) => ({
        isAuthenticated: false,
        authToken: null,
        refreshToken: null,
        user: null,
        authUserId: null,
        referrer: null,
        setup2FAToken: null,

        clearAuthDataStorage: () => {
          set({
            isAuthenticated: false,
            authToken: null,
            user: null,
            refreshToken: null,
            authUserId: null,
            referrer: null,
          })
        },

        clearSetup2FAToken: () => {
          set({ setup2FAToken: null })
        },

        /**
         * @param access_token - access token
         * @param newUser - new user object
         * @returns {Promise<{user: Partial<IUser>}>}
         * @description - change organization function lets you switch between companies
         */

        changeOrganization: async (access_token, newUser) => {
          const token = access_token

          set({
            authToken: token,
            isAuthenticated: true,
            user: newUser,
          })

          return {
            access_token: access_token,
            user: newUser,
          }
        },
        /**
         *
         * @param userData
         * @description - This function allows you to extend the current user object with additional properties. It merges the existing user state with the new data provided, ensuring that any new or updated properties are reflected in the user state.
         */
        extendUser: (userData) => {
          set((state) => ({
            user: {
              ...state.user,
              ...userData,
            } as IUser & Partial<UserExtension>,
          }))
        },

        checkUserSession: async () => {
          const response = (await request.get(
            { url: "/account/checksession" },
            "newArchAxiosInstance"
          )) as IBaseRes<CheckUserSession>

          const { status, access_token, refresh_token } = response?.data || {}

          if (response?.success || status === "Success") {
            set({
              authToken: access_token,
              refreshToken: refresh_token,
              isAuthenticated: true,
            })
          }

          return { status, access_token, refresh_token } as CheckUserSession
        },
        signOut: (keepRedirect = false) => {
          const referrer = get().referrer
          // const referrer = localStorage.getItem("referrer")
          const presentLocation = window.location.href

          get().clearAuthDataStorage()

          if (!!referrer) {
            window.location.href = referrer
          } else if (keepRedirect) {
            if (presentLocation?.includes("auth")) {
              //keep on the page
            } else if (hasLoggedInBefore()) {
              useAuthModalStore.getState().open()
            } else {
              window.location.href = `/auth/login?redirect=${encodeURIComponent(
                presentLocation
              )}`
            }
          } else {
            try {
              sessionStorage.removeItem(HAS_LOGGED_IN_BEFORE)
            } catch (_) {
              /* ignore */
            }
            window.location.href = `/auth/login`
          }
          // localStorage.removeItem("referrer")
        },
      }),
      {
        name: `${APP_ID}.auth`,
        partialize: (state) => ({
          isAuthenticated: state.isAuthenticated,
          authToken: state.authToken,
          refreshToken: state.refreshToken,
          user: state.user,
          authUserId: state.authUserId,
          referrer: state.referrer,
        }),
      }
    )
  )
)

export async function fetchUserSession(): Promise<{ user: IUser }> {
  const response = (await request.get(
    { url: "/account/getusersession" },
    "newArchAxiosInstance"
  )) as IBaseRes<IUser>

  const user = (response?.data || {}) as IUser

  useAuthStore.setState({ user })

  return { user }
}
